// SlashMenu state machine — pure logic, no DOM.
// Handoff §4: the menu is modality changes only. Agent *acts* on a live
// page (TinyFish browser automation), which is a new modality, so it
// earns a slot. Settings and model pickers still go in ⌘K.
// Order: Image (most different) / Search / Agent / Attach (drag-and-drop
// teacher stays last). TinyFish Search+Fetch remains a stage under Search.

export type SlashItemId = "image" | "search" | "browse" | "attach";

export interface SlashMenuItem {
  id: SlashItemId;
  label: string;
  description: string;
  command: `/${string}`;
}

export const SLASH_MENU_ITEMS: readonly SlashMenuItem[] = [
  {
    id: "image",
    label: "Image",
    description: "Generate an image",
    command: "/image",
  },
  {
    id: "search",
    label: "Search",
    description: "Force web search",
    command: "/search",
  },
  {
    id: "browse",
    label: "Agent",
    description: "Act on a website",
    command: "/browse",
  },
  {
    id: "attach",
    label: "Attach",
    description: "Or drag and drop",
    command: "/file",
  },
] as const;

import type { SearchProviderId } from "../searchProviders";

/**
 * `items` is the locked three-item list; `providers` is the Search submenu.
 * Closing always returns to `items` so the menu never reopens mid-flow.
 */
export type SlashMenuStage = "items" | "providers";

export interface SlashMenuState {
  readonly open: boolean;
  readonly stage: SlashMenuStage;
}

export const initialSlashMenuState = (): SlashMenuState => ({
  open: false,
  stage: "items",
});

export type SlashMenuAction =
  | { kind: "open" }
  | { kind: "close" }
  | { kind: "toggle" }
  | { kind: "escape" }
  | { kind: "outsideClick" }
  | { kind: "selectItem"; itemId: SlashItemId }
  | { kind: "selectProvider"; providerId: SearchProviderId };

const CLOSED: SlashMenuState = { open: false, stage: "items" };

export function reduceSlashMenu(
  state: SlashMenuState,
  action: SlashMenuAction,
): SlashMenuState {
  switch (action.kind) {
    case "open":
      return state.open ? state : { open: true, stage: "items" };
    case "close":
    case "outsideClick":
      return state.open ? CLOSED : state;
    case "selectItem":
      if (!state.open) return state;
      // Search opens the provider stage instead of committing; the other two
      // items still act immediately.
      return action.itemId === "search"
        ? { open: true, stage: "providers" }
        : CLOSED;
    case "selectProvider":
      return state.open ? CLOSED : state;
    case "escape":
      if (!state.open) return state;
      // Escape from the submenu steps back rather than closing outright —
      // otherwise a mis-click on Search costs the whole menu.
      return state.stage === "providers"
        ? { open: true, stage: "items" }
        : CLOSED;
    case "toggle":
      return state.open ? CLOSED : { open: true, stage: "items" };
  }
}

// Inline `/`-typing trigger: opens the same menu when the user types
// `/` as the first character of an otherwise-empty input. Two entry
// paths (click + slash), one menu, identical behavior.
//
// The trigger requires:
//   - text starts with `/`
//   - cursor is positioned within the slash command (no space yet)
// Anything past the first space (`/image hello`) means the command
// was committed and the menu should already have closed.
export function detectSlashTrigger(value: string, caretPos: number): boolean {
  if (value.length === 0) return false;
  if (!value.startsWith("/")) return false;
  const beforeCaret = value.slice(0, caretPos);
  if (beforeCaret.includes(" ")) return false;
  return true;
}
